import moment from 'moment'
import { v4 as uuid } from 'uuid'

// Action Types
export const SET_ACTIVE_USERNAME = 'realtime/setActiveUsername'
export const COLLAPSE_CHAT = 'realtime/collapseChat'
export const EXPAND_CHAT = 'realtime/expandChat'
export const BEGIN_CHAT = 'realtime/beginChat'
export const ADD_MESSAGE_TO_CHAT = 'realtime/addMessageToChat'
export const END_CHAT = 'realtime/endChat'
export const HYDRATE_CHAT_HISTORY = 'realtime/hydrateChatHistory'
export const SET_CHAT_HISTORY_LOADING = 'realtime/setChatHistoryLoading'
// Agent Assist (raised-hand coaching) panel state.
export const RAISE_HAND = 'realtime/raiseHand'
export const OPEN_ASSIST_PANEL = 'realtime/openAssistPanel'
export const CLOSE_ASSIST_PANEL = 'realtime/closeAssistPanel'
export const TOGGLE_ASSIST_PANEL = 'realtime/toggleAssistPanel'

// Every open chat is the same conversation wherever it's shown — the Agent
// Assist panel and CommandCenter's inline chat are two views of this one store.
// So the panel/launcher list every open chat, not a filtered subset.
export const selectOpenChats = (chats = {}) => Object.values(chats)

// Initial State
export const initialState = {
  // `collapsed` is retained for the legacy docked AlertSidebar; the Agent
  // Assist panel uses `panelOpen` instead (no minimize — it's open or closed).
  collapsed: true,
  panelOpen: false,
  chats: {},
  activeUsername: null,
  // Monotonic counter stamped onto each chat's `raisedSeq` so the pill list can
  // hold a frozen raise-order without reshuffling on every new message.
  raiseCounter: 0,
}

const newChatRecord = (action, { raisedSeq, unread }) => ({
  messages: [],
  alertId: action.alertId,
  callId: action.callId || null,
  agentId: action.agentId,
  managerId: action.managerId,
  agentUsername: action.agentUsername,
  agentLastName: action.agentLastName,
  agentFirstName: action.agentFirstName,
  managerUsername: action.managerUsername,
  managerFirstName: action.managerFirstName,
  managerLastName: action.managerLastName,
  raisedSeq,
  unread,
  hasMore: true,
  historyLoading: false,
  historyLoaded: false,
})

// Reducer
export default function realtimeChatsReducer(state = initialState, action) {
  switch (action.type) {
    case SET_ACTIVE_USERNAME: {
      // Selecting an agent clears its unread badge (the supervisor is now
      // looking at the thread).
      const existing = state.chats[action.activeUsername]
      return {
        ...state,
        activeUsername: action.activeUsername,
        chats: existing
          ? { ...state.chats, [action.activeUsername]: { ...existing, unread: 0 } }
          : state.chats,
      }
    }
    case COLLAPSE_CHAT:
      return { ...state, collapsed: true }
    case EXPAND_CHAT:
      return { ...state, collapsed: false }
    case OPEN_ASSIST_PANEL:
      return { ...state, panelOpen: true }
    case CLOSE_ASSIST_PANEL:
      return { ...state, panelOpen: false }
    case TOGGLE_ASSIST_PANEL:
      return { ...state, panelOpen: !state.panelOpen }
    case RAISE_HAND: {
      // A raised_hand event puts the agent in the Agent Assist list (badges the
      // launcher) WITHOUT forcing the panel open. A repeat raise (e.g. after
      // the manager closed the chat) re-adds them with a fresh raisedSeq so
      // they sort to the end of the frozen raise order, and bumps unread.
      const existing = state.chats[action.agentUsername]
      const raisedSeq = state.raiseCounter + 1
      return {
        ...state,
        raiseCounter: raisedSeq,
        chats: {
          ...state.chats,
          [action.agentUsername]: existing
            ? {
                ...existing,
                // A re-raise can be on a NEW call — refresh the live-call
                // context (chat sends and the header Listen both embed it)
                // without touching the thread.
                callId: action.callId,
                alertId: action.alertId,
                managerId: action.managerId,
                managerUsername: action.managerUsername,
                raisedSeq,
                unread: (existing.unread || 0) + 1,
              }
            : newChatRecord(action, { raisedSeq, unread: 1 }),
        },
      }
    }
    case BEGIN_CHAT: {
      // Create-or-activate a chat. Idempotent: if the agent already has a
      // record (e.g. RAISE_HAND created it on the socket event), just activate
      // + clear unread; never wipe an existing thread. Does NOT open the Agent
      // Assist panel — callers that want it open (the bell) dispatch
      // OPEN_ASSIST_PANEL alongside, so a Live Listen row can seed a
      // record without popping the panel.
      const existing = state.chats[action.agentUsername]
      const raisedSeq = existing ? existing.raisedSeq : state.raiseCounter + 1
      return {
        ...state,
        raiseCounter: existing ? state.raiseCounter : raisedSeq,
        activeUsername: action.agentUsername,
        chats: {
          ...state.chats,
          [action.agentUsername]: existing
            ? { ...existing, unread: 0 }
            : newChatRecord(action, { raisedSeq, unread: 0 }),
        },
      }
    }
    case SET_CHAT_HISTORY_LOADING: {
      const existing = state.chats[action.agentUsername]
      if (!existing) {
        return state
      }
      return {
        ...state,
        chats: {
          ...state.chats,
          [action.agentUsername]: {
            ...existing,
            historyLoading: action.historyLoading,
          },
        },
      }
    }
    case ADD_MESSAGE_TO_CHAT: {
      // if chat has not begun yet then return state.
      const existing = state.chats[action.agentUsername]
      if (!existing) return state

      const existingMessages = existing.messages
      // De-dupe by chatId so we don't render the same chat twice when it
      // arrives over both the RTC and CC sockets. Live sends from the local
      // tab don't carry a chatId so they always go through.
      if (
        action.chatId &&
        existingMessages.some((messageItem) => messageItem.id === action.chatId)
      ) {
        return state
      }

      // An agent message to a chat that isn't the open, active one bumps its
      // unread badge (shown in place — the list never reshuffles for it).
      const isActiveAndOpen = state.panelOpen && state.activeUsername === action.agentUsername
      const unread =
        !action.isMessageFromManager && !isActiveAndOpen
          ? (existing.unread || 0) + 1
          : existing.unread

      return {
        ...state,
        chats: {
          ...state.chats,
          [action.agentUsername]: {
            ...existing,
            unread,
            messages: [
              ...existingMessages,
              {
                id: action.chatId,
                // Local optimistic sends don't carry a server-assigned chatId.
                // Stamping a stable localId here lets the chat list use a real
                // React key instead of re-rolling uuid on every render (which
                // unmounted every row and reset scroll position).
                localId: action.chatId ? undefined : uuid(),
                message: action.message,
                date: action.date,
                isMessageFromManager: action.isMessageFromManager,
              },
            ],
          },
        },
      }
    }
    case HYDRATE_CHAT_HISTORY: {
      const existing = state.chats[action.agentUsername]
      if (!existing) {
        return state
      }

      const existingMessages = existing.messages || []
      const existingIds = new Set(
        existingMessages.map((messageItem) => messageItem.id).filter(Boolean)
      )

      const historyMessages = action.messages
        .filter((messageItem) => !existingIds.has(messageItem.id))
        .map((messageItem) => {
          // Server stores created_at as naive UTC; parse as UTC then convert
          // to the viewer's local zone so the format matches the live messages.
          const messageMoment = moment.utc(messageItem.created_at).local()
          // Same-day messages just show the time; older messages get the date
          // prefix so a manager scrolling back through history can tell when
          // something was sent.
          const date = messageMoment.isSame(moment(), 'day')
            ? messageMoment.format('LT')
            : messageMoment.format('MMM D, YYYY · LT')
          return {
            id: messageItem.id,
            message: messageItem.message,
            isMessageFromManager: !messageItem.from_agent,
            date,
            // Raw ISO retained so the scroll-to-top handler has a `before`
            // cursor for the next history page.
            createdAtIso: messageItem.created_at,
          }
        })
        .reverse()

      return {
        ...state,
        chats: {
          ...state.chats,
          [action.agentUsername]: {
            ...existing,
            messages: [...historyMessages, ...existingMessages],
            hasMore: action.hasMore,
            // historyLoaded flips true the first time we successfully hydrate
            // (initial page). The component watches this transition to scroll
            // the new content to the bottom.
            historyLoaded: existing.historyLoaded || action.isInitial,
          },
        },
      }
    }
    case END_CHAT: {
      // Manual close — the manager is done helping this agent. Drops the chat
      // (and its pill) entirely; a later re-raise re-creates it.
      const chatItems = { ...state.chats }
      delete chatItems[action.username]
      let { activeUsername } = state
      if (activeUsername === action.username) {
        if (Object.keys(chatItems).length > 0) {
          // eslint-disable-next-line prefer-destructuring
          activeUsername = [...Object.keys(chatItems)][0]
        } else {
          activeUsername = null
        }
      }

      return { ...state, chats: chatItems, activeUsername }
    }
    default:
      return state
  }
}
